import classNames from "classnames";
import { User, Userv2 } from "models/user";
import Link from "next/link";
import { useRouter } from "next/router";
import { FocusEventHandler, PropsWithChildren, Ref, useEffect, useRef, useState } from "react";
import Logo from "./Logo";
import { UserAvatar } from "./User";
import NavbarUser from 'components/navbar/User'
import Notifications from "./navbar/Notifications";
import Button from "./Button";
import { PropsWithClass, PropsWithClassAndStyle, PropsWithPrefixClass } from "utils/props";
import { XOR } from "utils/xor";
import { twMerge } from "tailwind-merge";
import { useUser } from "hooks/user";
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  size,
  useHover,
  useDismiss,
  useRole,
  useInteractions,
  Placement
} from '@floating-ui/react';

type NavbarLinkProps = { dynamic?: boolean; href: string; }

const navbarLinkCx = classNames(
  "text-black",
  "font-normal",
  "hover:text-secondary",
  "p-0.5",
  "hover:cursor-pointer",
  "border border-transparent hover:border-secondary pl-0.5 rounded-md hover:cursor-pointer",
  "!no-underline",
)

const navbarSectionContainerCx = classNames(
  "bg-white hidden group-hover/section:flex flex-col py-1 px-0.5 rounded-md shadow-inplace-xl w-13" // FIXME: should be w-15. But RSCI and VAK go out of bounds.
)

export function NavbarLink({
  dynamic,
  children,
  href,
  arrow,
  className,
  style,
}: PropsWithClassAndStyle<PropsWithChildren<NavbarLinkProps & { arrow?: boolean }>>) {
  const router = useRouter();
  const element = (
    <a
      className={twMerge(navbarLinkCx, className)}
      style={style}
      key={href}
      href={href}
    >
      {children}

    </a>
  );
  return dynamic ? (
    <Link href={href} passHref>
      {element}
    </Link>
  ) : (
    element
  );
}

export function NavbarSection({ children, name, link }: PropsWithChildren<{ name: string; link?: NavbarLinkProps }>) {
  return <div className="relative group/section"> {/* FIXME: something weird is going on with selecting & pointer cursor on this element. Too bad! */}
    {!!link ? <NavbarLink {...link} arrow={!!children}>{name}</NavbarLink> :
      <h2 className="group-hover/section:text-secondary font-normal max-2xl:text-base text-black hover:cursor-pointer">
        {name}
        {!!children && <img src="/images/arrow-down.svg" className="group-hover/section:rotate-180 transition-transform duration-200 inline-block ml-0.5" width={12} height={12} />}
      </h2>}
    {!!children && <div className="mt-1 pt-1 absolute top-0 left-0 z-40">
      <div className={navbarSectionContainerCx}>
        {children}
      </div>
    </div>}
  </div>
}

export function NavbarLinkGroup({ children, name, link, childrenClassName }: PropsWithChildren<{ name: string; link?: NavbarLinkProps }> & PropsWithPrefixClass<{}, "children">) {
  return <div className={twMerge(navbarLinkCx, "relative group justify-between items-center flex gap-0.5 flex-row w-full")}>
    <span>{name}</span>
    <img src="/images/arrow-down.svg" className="w-[16px] h-[16px] -rotate-90" />
    <span className="left-12 top-0 absolute hidden group-hover:flex">
      <div className={twMerge("ml-0.5", navbarSectionContainerCx, childrenClassName)}>
        {children}
      </div>
    </span>
  </div>
}


export function NavbarLinkGroupV2({ children, name, link, childrenClassName, childrenWidth }: PropsWithChildren<{ name: string; link?: NavbarLinkProps; childrenWidth?: number | string }> & PropsWithPrefixClass<{}, "children">) {
  const [isOpen, setIsOpen] = useState(false);
  const [placement, setPlacement] = useState<Placement>('right-start');

  // Update placement based on screen size
  useEffect(() => {
    const handleResize = () => {
      setPlacement(window.innerWidth < 1200 ? 'left-start' : 'right-start');
    };

    handleResize(); // Set initial value
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const customSize = childrenWidth ? size({
    apply({ elements }) {
      Object.assign(elements.floating.style, {
        width: typeof childrenWidth === 'number'
          ? `${childrenWidth}px`
          : childrenWidth,
        maxWidth: 'calc(100vw - 20px)'
      });
    }
  }) : null;

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(0),
      flip(),
      shift({  }),
      ...(customSize ? [customSize] : [])
    ],
  });

  const hover = useHover(context, { move: false });
  const dismiss = useDismiss(context);
  const role = useRole(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    dismiss,
    role
  ]);

  return (
    <div
      ref={refs.setReference}
      {...getReferenceProps()}
      className={twMerge(navbarLinkCx, "relative group justify-between items-center flex gap-0.5 flex-row w-full")}
    >
      <span>{name}</span>
      <img
        src="/images/arrow-down.svg"
        className={`w-[16px] h-[16px] transition-transform -rotate-90`}
        alt="dropdown indicator"
      />

      {isOpen && (
        <div
          ref={refs.setFloating}
          style={floatingStyles}
          {...getFloatingProps()}
          className={twMerge(navbarSectionContainerCx, childrenClassName)}
        >
          {children}
        </div>
      )}
    </div>
  );
}



export function NavbarLinks({ user }: { user: Userv2 | undefined }) {
  return <div className="gap-3 max-2xl:gap-2 max-xl:gap-1 md:flex justify-center items-center hidden">
    <NavbarSection name="Авторам">
      {/* <NavbarLink href="#">Преимущества</NavbarLink> */}
      {/* <NavbarLink href="#">Проверить публикацию</NavbarLink> */}
      {/* <NavbarLink href="#">Стать автором</NavbarLink> */}
      <NavbarLink href="/info/services" dynamic>Преимущества</NavbarLink>
      <NavbarLink href="/journals" dynamic>Журналы платформы</NavbarLink>
      <NavbarLink href="/user-guide">Руководство пользователя</NavbarLink>
      <NavbarLink href="/intellichecker">Проверить рукопись</NavbarLink>
      {/* <NavbarLink href="#">Рекоммендации по написанию статей</NavbarLink> */}
      <NavbarLinkGroupV2 name="Подготовка статьи к публикации" childrenWidth={400}>
        {/* <NavbarLink href="/static/publication-preparation/scientific-reviews.pdf">Научные обзоры</NavbarLink> */}
        {/* <NavbarLink href="/static/publication-preparation/infographics.pdf">Инфографика в статье</NavbarLink> */}
        {/* <NavbarLink href="/static/publication-preparation/creating-written-report-article.pdf">Создание письменного отчета/статьи</NavbarLink> */}
        <NavbarLink href="/publication-preparation/scientific-reviews">Научные обзоры</NavbarLink>
        <NavbarLink href="/publication-preparation/infographics">Инфографика в статье</NavbarLink>
        <NavbarLink href="/publication-preparation/creating-written-report-article">Создание письменного отчета/статьи</NavbarLink>
        <NavbarLink href="/publication-preparation/what-is-soa-review">Обзор современного состояния проблемы</NavbarLink>
        {/* <NavbarLink href="https://gnicpm.ru/wp-content/uploads/2020/08/e-high-quality_clinical_practice5-3.pdf">Методологические основы клинического исследования</NavbarLink> */}
      </NavbarLinkGroupV2>
      <NavbarLink href="http://51.250.66.81:8085/index.php/Заглавная_страница">Справка</NavbarLink>
    </NavbarSection>
    <NavbarSection name="Рецензентам">
      <NavbarLink href="/info/reviewers" dynamic>Стать подтвержденным рецензентом</NavbarLink>
      {!!user && <NavbarLink href={encodeURI('/profile/@me?tab=reviews')} dynamic>Мои рецензии</NavbarLink>}
    </NavbarSection>
    <NavbarSection name="Редакторам">
      <NavbarLink href="/info/editors" dynamic>Стать редактором</NavbarLink>
      {!!user && !!user.journal_id && <NavbarLink href={encodeURI('/journals/@me')} dynamic>Мой журнал</NavbarLink>}
    </NavbarSection>
    <NavbarSection name="Ресурсы">
      {/* <NavbarLink href="/static/guide-v3.pdf">Руководство пользователя</NavbarLink> */}
      <NavbarLink href="https://prevmed.elpub.ru/">Терапия без границ</NavbarLink>
      <NavbarLinkGroupV2 name="Реестры журналов">
        <NavbarLink href="https://vak.gisnauka.ru/ru/s3-files/01cc80c69fae4988a0246a8f5e2774e7:fisgna/public/media/uploaded/news_files/2094e02c-d851-48cd-9d57-fe7ebd34a039/%D0%9F%D0%B5%D1%80%D0%B5%D1%87%D0%B5%D0%BD%D1%8C__%D0%BD%D0%B0_01.07.2025.pdf.pdf">Перечень ВАК</NavbarLink>
        <NavbarLink href="https://journalrank.rcsi.science/ru/record-sources/">Белый список</NavbarLink>
        {/* <NavbarLink href="https://www.elibrary.ru/projects/rsci/rsci.pdf">Список журналов, входящих в базу данных RSCI</NavbarLink> */}
        <NavbarLink href="/sci-pub-catalogs/rsci">Список журналов, <br /> входящих в базу данных RSCI</NavbarLink>
      </NavbarLinkGroupV2>
      <NavbarLink href="/courses" dynamic>Курсы, вебинары, обучающие мероприятия</NavbarLink>
    </NavbarSection>
  </div>
}



// export function NavbarLinks() {
//   const list: [
//     string, string,
//     [string, string][]
//   ][] = [
//       ["Авторам", "/#", [
//         ['']
//       ]],
//       ["Рецензентам", "/#", []],
//       ["Редакторам", "/#", []],
//       // ["Ресурсы", `/journals/${process.env.NEXT_PUBLIC_TEST_JOURNAL_ID}`],

//       // ["Регистрация", "/#"],
//       ["Ресурсы", "/#", []],
//       // ["Издательствам", "/#"],
//       // ["Research Blog", "/#"],
//     ];

//   return (
//     <div className="gap-3 md:flex justify-center items-center hidden">
//       {list.map(([name, link, sub]) => (
//         <div className="relative group">
//           <NavbarLink href={link} dynamic key={link} name={name} arrow={sub.length > 0} />
//           {sub.length > 0 && <div className="bg-white hidden group-hover:flex absolute mt-0.5 p-1 rounded-md shadow-inplace-xl">
//           </div>}

//         </div>
//       ))}
//     </div>
//   );
// }

export function Navbar({ variant, static: fixed, user, className, style, version = 'v1', showParentLogos }: PropsWithClassAndStyle<XOR<{ variant: 'absolute' | 'fixed' }, { static: boolean }> & { user?: Userv2; version?: 'v1' | 'v2'; showParentLogos?: boolean }>) {
  const router = useRouter()
  return (
    <nav
      className={classNames(
        className,
        "py-0.5 px-3 max-3xl:px-1",
        "flex",
        "items-center",
        {
          "justify-between md:justify-center md:gap-1 lg:justify-between lg:gap-0": version === 'v1',
          // 'justify-center gap-17': version === 'v2'
          // 'justify-between 4xl:px-14': version === 'v2'
          'justify-between 4xl:justify-center 4xl:gap-17': version === 'v2'
          // 'justify-center gap-17': version === 'v2'
        },
        // "border-b-[1px] border-b-light-grey",
        "w-full",
        // "t-0",
        { [!!fixed || !variant ? 'static' : variant]: true }, // TODO: rename fixed to static
      )}
      data-test="navbar"
      style={style}
    >
      <div className="flex flex-row gap-1 items-center relative">
        <Logo width={104} height={95} text layout="fixed" className="h-4 w-auto" />
        {showParentLogos && <>
          <a href="https://gnicpm.ru/" className="h-2 left-8 w-auto absolute" target="_blank" rel="noreferrer"><img src="/images/nmic.png" className="h-2 w-auto" /></a>
          <a href="https://ropniz.ru?rhredir=1" className="left-5 h-2 w-auto absolute" target="_blank" rel="noreferrer"><img src="/images/ropniz.png" className="h-2 w-auto" /></a>
        </>}
      </div>
      <NavbarLinks user={user} />
      <div className="flex flex-row gap-1 items-center">
        {user ? <>
          <Notifications />
          <NavbarUser user={user} />
        </> : <>
          <Button onClick={() => router.push('/register')} colorscheme="light" data-test="register">Регистрация</Button>
          <Button onClick={() => router.push('/login')} data-test="login">Войти</Button>
        </>}
      </div>
    </nav>
  );
}

